import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import AllFields from "./AllFields";

const FIELDS_PER_PAGE = 9;

const FilterFields = ({
  fieldStats = [],
  sensorsData = [],
  filteredByStatus = [],
  crops = [],
  sensorStats = [],
  value,
  setValue,
}) => {
  const { t } = useTranslation();
  const [searchName, setSearchName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const fields = useMemo(
    () =>
      fieldStats.flatMap((farm) =>
        (farm.fields || []).map((field) => ({
          name: field.name,
          status: field.status,
          description: field.description,
          uid: field.uid,
          farm_id: field.farm_id,
          id: field.id,
          sensors: field.sensors,
        }))
      ),
    [fieldStats]
  );

  const sensorReadingsForField = (field) => {
    if (!Array.isArray(field.sensors)) return [];
    return sensorsData.filter((reading) =>
      field.sensors.some(
        (sensor) => sensor.id === reading.sensor_id || sensor.code === reading.code
      )
    );
  };

  const sensorStatusesForField = (field) => {
    if (!Array.isArray(field.sensors)) return [];
    return field.sensors
      .map((fieldSensor) =>
        sensorStats.find(
          (sensor) =>
            sensor.id === fieldSensor.id || sensor.code === fieldSensor.code,
        )?.connectivityStatus?.state,
      )
      .filter(Boolean);
  };

  const onlineFields = fields.filter((field) => {
    const statuses = sensorStatusesForField(field);
    if (statuses.length) return statuses.includes("online");
    return sensorReadingsForField(field).some(
      (reading) => moment().diff(moment(reading.time), "minutes") <= 1440,
    );
  });

  const lowBatteryFields = fields.filter((field) =>
    sensorStatusesForField(field).includes("low_battery"),
  );

  const offlineFields = fields.filter((field) => {
    const statuses = sensorStatusesForField(field);
    if (statuses.length) return statuses.every((status) => status === "offline");
    const readings = sensorReadingsForField(field);
    return readings.length > 0 && readings.every(
      (reading) => moment().diff(moment(reading.time), "minutes") > 1440
    );
  });

  const fieldsWithoutSensors = fields.filter(
    (field) => !Array.isArray(field.sensors) || field.sensors.length === 0
  );

  const fieldsForSelectedFilter = value === "No sensor"
    ? fieldsWithoutSensors
    : value === "Online"
    ? onlineFields
    : value === "Offline"
    ? offlineFields
    : value === "low_batt"
    ? lowBatteryFields
    : fields;

  const filteredFields = fieldsForSelectedFilter.filter((field) => {
    if (searchName && !field.name?.toLowerCase().includes(searchName.toLowerCase())) return false;
    if (["Critical", "Optimal", "Full"].includes(value)) return field.status === value;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredFields.length / FIELDS_PER_PAGE));
  const visiblePage = Math.min(currentPage, totalPages);
  const paginatedFields = filteredFields.slice(
    (visiblePage - 1) * FIELDS_PER_PAGE,
    visiblePage * FIELDS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, value]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const userId = filteredByStatus[0]?.user_id;

  const filters = [
    { value: "", label: t("All_fields") },
    { value: "Critical", label: t("Critical") },
    { value: "Optimal", label: t("Optimal") },
    { value: "Full", label: t("Full") },
    { value: "No sensor", label: t("No_sensor") },
    { value: "Online", label: t("Online") },
    { value: "Offline", label: t("Offline") },
    { value: "low_batt", label: t("low_batt") },
  ];

  return (
    <div className="overview-fields-workspace">
      <div className="overview-fields-toolbar">
        <label className="overview-fields-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            value={searchName}
            onChange={(event) => setSearchName(event.target.value)}
            placeholder={t("Search_by_Field_Name")}
          />
          {searchName && (
            <button type="button" onClick={() => setSearchName("")} aria-label="Clear">
              <i className="fas fa-times" />
            </button>
          )}
        </label>

        <label className="overview-fields-filter">
          <i className="fas fa-sliders-h" aria-hidden="true" />
          <select value={value || ""} onChange={(event) => setValue(event.target.value)}>
            {filters.map((filter) => (
              <option key={filter.value} value={filter.value}>{filter.label}</option>
            ))}
          </select>
        </label>

        <span className="overview-fields-result-count">
          <strong>{filteredFields.length}</strong>
          <span>{t("fields")}</span>
        </span>
      </div>

        <div className="overview-fields-results">
          <AllFields
            sensorsData={sensorsData}
            crops={crops}
            filteredFields={paginatedFields}
            userId={userId}
          />

          {filteredFields.length > FIELDS_PER_PAGE && (
            <nav
              className="overview-fields-pagination"
              aria-label={t("page", { defaultValue: "Page" })}
            >
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={visiblePage === 1}
                aria-label={t("previous", { defaultValue: "Previous" })}
              >
                <i className="fas fa-chevron-left" aria-hidden="true" />
              </button>
              <span>
                {t("page", { defaultValue: "Page" })} <strong>{visiblePage}</strong> / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={visiblePage === totalPages}
                aria-label={t("next", { defaultValue: "Next" })}
              >
                <i className="fas fa-chevron-right" aria-hidden="true" />
              </button>
            </nav>
          )}
        </div>
    </div>
  );
};

export default FilterFields;
